import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import styled from '@emotion/styled';
import { L } from '../../../../lib/i18n';
import { Checkbox } from '../../../../components/Checkbox/Checkbox';
import { TableMeta } from '../../TableMetaTypes/ProductTemplatesTableMeta';
import { RouterOutputs } from '@app/trpc-api';
import RadixSelect from '../../../../components/RadixSelect/RadixSelect';

const columnHelper = createColumnHelper<RouterOutputs['BindersApi']['getTemplateProductsList']['items'][0]>();

const CellContainer = styled.div({
    display: 'grid',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    maxWidth: '100%',
    fontSize: '0.75rem',
    gap: '0.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Flex = styled.div({
    display: 'flex',
});

const Data = styled.p({
    margin: '0px',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const ItalicSpan = styled.span({
    fontStyle: 'italic',
});

const BoldSpan = styled.span({
    fontWeight: 'bolder',
});

const columns = [
    columnHelper.display({
        id: 'selection',
        header: ({ table }) => (
            <Checkbox checked={table.getIsAllRowsSelected()} onChange={table.getToggleAllRowsSelectedHandler()} />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
        size: 30,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
        enableResizing: false,
    }),
    columnHelper.accessor('name', {
        cell: ({ row }) => (
            <CellContainer>
                <Data>{row.original.name ? L(row.original.name) : <ItalicSpan>{L('name_missing')}</ItalicSpan>}</Data>
            </CellContainer>
        ),
        minSize: 135,
        size: 150,
        header: () => <Flex>{L('product_group')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
    columnHelper.accessor('function', {
        cell: ({ row }) => (
            <CellContainer>
                <Data>
                    {row.original?.function && row.original.function.length > 0 ? L(row.original.function) : ''}
                </Data>
            </CellContainer>
        ),
        minSize: 90,
        size: 150,
        header: () => <Flex>{L('function')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
    columnHelper.accessor('product_name', {
        cell: ({ row }) =>
            row.original?.count ? (
                <CellContainer>
                    <Data>
                        {row.original?.product_name ? (
                            row.original.product_name
                        ) : (
                            <ItalicSpan>{L('name_missing')}</ItalicSpan>
                        )}{' '}
                        <BoldSpan>{`(${row.original?.count}C)`}</BoldSpan>
                    </Data>
                </CellContainer>
            ) : (
                <CellContainer>
                    <Data>{row.original?.product_name}</Data>
                </CellContainer>
            ),
        minSize: 80,
        size: 150,
        header: () => <Flex>{L('product_title')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
    columnHelper.accessor('color', {
        cell: ({ row }) => <CellContainer>{row.original.color ? L(row.original?.color) : ''}</CellContainer>,
        minSize: 60,
        size: 150,
        header: () => <Flex>{L('product_color')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
    columnHelper.accessor('brand', {
        cell: ({ row }) => (
            <CellContainer>
                <Data>{row.original?.brand ? L(row.original.brand) : ''}</Data>
            </CellContainer>
        ),
        minSize: 115,
        size: 150,
        header: () => <Flex>{L('brand')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
    columnHelper.accessor('source', {
        cell: ({ row }) => (
            <CellContainer>
                <Data>{row.original.source ? L(row.original.source) : ''}</Data>
            </CellContainer>
        ),
        minSize: 115,
        size: 150,
        header: () => <Flex>{L('source')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
    columnHelper.accessor('package_type', {
        cell: ({ row, table }) => (
            <CellContainer style={{ gridTemplateColumns: '1fr', width: '100%' }}>
                <RadixSelect
                    value={row.original.package_type}
                    options={(table.options.meta as TableMeta).packageTypes.map((item) => ({
                        value: item.name,
                        label: L(item.name),
                    }))}
                    onValueChange={async (val) =>
                        await (table.options.meta as TableMeta).updatePackageType(val, row.original.id)
                    }
                    resetValue={async () =>
                        await (table.options.meta as TableMeta).updatePackageType(null, row.original.id)
                    }
                />
            </CellContainer>
        ),
        minSize: 115,
        size: 150,
        header: () => <Flex>{L('choice')}</Flex>,
        meta: {
            styles: {
                flex: '1 0 auto',
            },
        },
    }),
];

export default columns;
